import { request, requestEmpty } from './client'
import {
  asArray,
  asObject,
  bool,
  count,
  num,
  objects,
  optBool,
  optNum,
  optStr,
  optTime,
  raw,
  str,
  strings,
  text,
  time,
} from './parse'

/** How a message got into the conversation. See `ports.Direction`. */
export type Direction = 'client_inbound' | 'manual_outbound' | 'bot_outbound'

/**
 * An amount, with the difference between a figure and a guess preserved.
 *
 * `amountMinor` is null exactly when `kind` is `unknown`, which is the wire
 * contract and also the reason this is a type of its own: nothing in the panel
 * may reach for a number without passing the kind first.
 */
export interface Money {
  readonly amountMinor: number | null
  readonly currency: string
  readonly kind: string
}

export interface ThreadSummary {
  readonly id: string
  readonly contactId: string
  readonly displayName: string
  readonly username: string | undefined
  readonly locale: string | undefined
  readonly mode: string
  readonly unreadCount: number
  readonly lastInboundAt: string | undefined
  readonly lastOutboundAt: string | undefined
  readonly lastMessage: { readonly text: string; readonly direction: Direction; readonly at: string | undefined } | undefined
  /**
   * How many proposals are waiting on this conversation.
   *
   * Optional because the list endpoint does not send it yet. It is read rather
   * than derived: counting drafts by opening every conversation would turn one
   * screen into fifty requests, and guessing from the last message would put a
   * badge on rows that have no draft at all.
   */
  readonly pendingDrafts: number | undefined
  /** Work open against this conversation: a handoff, a refund, a lost session.
   * The server has always counted it and the panel used to drop it. */
  readonly openTasks: number | undefined
}

export interface ThreadPage {
  readonly threads: readonly ThreadSummary[]
  readonly total: number
  readonly limit: number
  readonly offset: number
}

export interface ThreadFilter {
  readonly mode?: string
  readonly unreadOnly?: boolean
  readonly waitingOnly?: boolean
  /** What somebody typed to find one conversation. */
  readonly query?: string
  readonly limit?: number
  readonly offset?: number
}

function parseMoney(value: unknown): Money | undefined {
  if (value === undefined || value === null) return undefined
  const source = asObject(value, 'money')
  const minor = source['amount_minor']
  return {
    amountMinor: typeof minor === 'number' ? minor : null,
    currency: str(source, 'currency'),
    kind: str(source, 'kind'),
  }
}

function parseDirection(value: string): Direction {
  return value === 'client_inbound' || value === 'manual_outbound' || value === 'bot_outbound'
    ? value
    : 'client_inbound'
}

function parseSummary(source: Record<string, unknown>): ThreadSummary {
  const last = source['last_message']

  return {
    id: str(source, 'id'),
    contactId: str(source, 'contact_id'),
    displayName: str(source, 'display_name'),
    username: optStr(source, 'username'),
    locale: optStr(source, 'locale'),
    mode: str(source, 'mode'),
    unreadCount: count(source, 'unread_count'),
    lastInboundAt: optTime(source, 'last_inbound_at'),
    lastOutboundAt: optTime(source, 'last_outbound_at'),
    lastMessage:
      last === undefined || last === null
        ? undefined
        : (() => {
            const record = asObject(last, '"last_message"')
            return {
              text: text(record, 'text'),
              direction: parseDirection(text(record, 'direction')),
              at: optTime(record, 'at'),
            }
          })(),
    pendingDrafts: optNum(source, 'pending_drafts'),
    openTasks: optNum(source, 'open_tasks'),
  }
}

export function fetchThreads(filter: ThreadFilter, signal?: AbortSignal): Promise<ThreadPage> {
  const query = new URLSearchParams()
  if (filter.mode !== undefined && filter.mode !== '') query.set('mode', filter.mode)
  if (filter.unreadOnly === true) query.set('unread', 'true')
  if (filter.waitingOnly === true) query.set('waiting', 'true')
  if (filter.query !== undefined && filter.query !== '') query.set('q', filter.query)
  if (filter.limit !== undefined) query.set('limit', String(filter.limit))
  if (filter.offset !== undefined && filter.offset > 0) query.set('offset', String(filter.offset))

  return request(
    `/api/threads?${query.toString()}`,
    (body) => {
      const source = asObject(body, 'the thread list')
      return {
        threads: objects(source, 'threads').map(parseSummary),
        total: count(source, 'total'),
        limit: count(source, 'limit'),
        offset: count(source, 'offset'),
      }
    },
    signal === undefined ? {} : { signal },
  )
}

export interface Message {
  readonly id: string
  readonly direction: Direction
  readonly platformMessageId: number
  readonly text: string
  readonly language: string | undefined
  readonly sentAt: string | undefined
  readonly receivedAt: string
  readonly processed: boolean
  readonly deleted: boolean
  readonly attachments: number
  readonly edits: number
}

export function fetchMessages(threadId: string, signal?: AbortSignal): Promise<readonly Message[]> {
  return request(
    `/api/threads/${encodeURIComponent(threadId)}/messages`,
    (body) => {
      const source = asObject(body, 'the messages')
      return objects(source, 'messages').map((message) => ({
        id: str(message, 'id'),
        direction: parseDirection(str(message, 'direction')),
        platformMessageId: num(message, 'platform_message_id'),
        text: text(message, 'text'),
        language: optStr(message, 'language'),
        sentAt: optTime(message, 'sent_at'),
        receivedAt: time(message, 'received_at'),
        processed: bool(message, 'processed'),
        deleted: bool(message, 'deleted'),
        attachments: count(message, 'attachments'),
        edits: count(message, 'edits'),
      }))
    },
    signal === undefined ? {} : { signal },
  )
}

export interface ThreadDetail {
  readonly id: string
  readonly contactId: string
  readonly accountId: string
  readonly mode: string
  readonly humanActiveUntil: string | undefined
  readonly inScope: boolean
  readonly unreadCount: number
  readonly lastInboundAt: string | undefined
  readonly lastOutboundAt: string | undefined
  /**
   * Set on a conversation somebody let in by hand (docs/adr/0012).
   *
   * `historyFrom` is the oldest message that was imported, not the start of the
   * conversation: the batch is bounded, and the card says so rather than letting
   * a manager believe they are reading everything.
   */
  readonly addedAt: string | undefined
  readonly historyFrom: string | undefined
}

export interface Contact {
  readonly id: string
  readonly displayName: string
  readonly username: string | undefined
  readonly locale: string | undefined
}

export interface SearchField {
  readonly field: string
  readonly value: unknown
  readonly confidence: number
  readonly importance: string
  readonly source: string
  readonly evidenceMessageIds: readonly string[]
  readonly confirmedAt: string | undefined
  readonly createdAt: string
}

export interface SearchRequest {
  readonly id: string
  readonly status: string
  readonly statusReason: string
  readonly version: number
  readonly unitType: string
  readonly preferredUnitType: string | undefined
  readonly startDateTarget: string | undefined
  readonly startDateEarliest: string | undefined
  readonly startDateLatest: string | undefined
  readonly endDateTarget: string | undefined
  readonly endDateEarliest: string | undefined
  readonly endDateLatest: string | undefined
  readonly durationMonthsHundredths: number | undefined
  readonly internalTermBand: string
  readonly peopleConfirmed: number
  readonly peoplePossible: number
  readonly budgetIdeal: Money | undefined
  readonly budgetMax: Money | undefined
  readonly budgetScope: string
  readonly billsScope: string
  readonly roomPrivateRequired: boolean | undefined
  readonly completeness: number
  readonly missingRequired: readonly string[]
  readonly riskFlags: readonly string[]
  readonly fields: readonly SearchField[]
}

export interface Draft {
  readonly id: string
  readonly threadId: string
  readonly version: number
  readonly status: string
  readonly kind: string
  readonly basedOnMessageId: string | undefined
  readonly body: string
  readonly editedBody: string | undefined
  readonly text: string
  readonly lang: string
  readonly reasonCodes: readonly string[]
  readonly preflight: Preflight | undefined
  readonly listingSnapshotIds: readonly string[]
  /** The pictures that will be sent with it, in the order they will arrive. */
  readonly photos: readonly DraftPhoto[]
  readonly createdByAiRunId: string | undefined
  readonly afterManual: boolean
  /** The Send button, decided by the server: `status === "pending"` and nothing else. */
  readonly approvable: boolean
  readonly approvedAt: string | undefined
  readonly sentAt: string | undefined
  readonly failureReason: string
  readonly createdAt: string
  readonly updatedAt: string
}

/** One picture a draft will send. */
export interface DraftPhoto {
  readonly assetId: string
  readonly listingId: string
  /** Our own media endpoint: the picture has not been near Telegram yet. */
  readonly url: string
  readonly mime: string
  readonly caption: string
}

export interface PreflightCheck {
  readonly name: string
  readonly passed: boolean
  readonly detail: string
  readonly retryAfterSec: number | undefined
}

export interface Preflight {
  readonly verdict: string
  readonly at: string | undefined
  readonly checks: readonly PreflightCheck[]
}

export interface Task {
  readonly id: string
  readonly kind: string
  readonly priority: string
  readonly status: string
  readonly dueAt: string | undefined
  readonly reason: string
  /** The messages the task came from. Empty for a task raised before they were recorded. */
  readonly evidenceMessageIds: readonly string[]
  readonly createdAt: string
}

export interface ThreadScreen {
  readonly thread: ThreadDetail
  readonly contact: Contact | undefined
  readonly searchRequest: SearchRequest | undefined
  readonly drafts: readonly Draft[]
  readonly tasks: readonly Task[]
  readonly tasksTruncated: boolean
}

/**
 * Read a list of code strings that the server sends as free-form JSON.
 *
 * `missing_required` and `risk_flags` are `json.RawMessage` columns: today they
 * hold arrays of strings, and the panel must not fall over on the day one of
 * them holds something else.
 */
function codes(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string')
}

function parsePreflight(value: unknown): Preflight | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value !== 'object' || Array.isArray(value)) return undefined

  const source = value as Record<string, unknown>
  const list = source['checks']

  return {
    verdict: typeof source['verdict'] === 'string' ? source['verdict'] : '',
    at: typeof source['at'] === 'string' ? source['at'] : undefined,
    checks: !Array.isArray(list)
      ? []
      : list.flatMap((entry): PreflightCheck[] => {
          if (typeof entry !== 'object' || entry === null) return []
          const check = entry as Record<string, unknown>
          const retry = check['retry_after_sec']
          return [
            {
              name: typeof check['name'] === 'string' ? check['name'] : '',
              passed: check['passed'] === true,
              detail: typeof check['detail'] === 'string' ? check['detail'] : '',
              retryAfterSec: typeof retry === 'number' ? retry : undefined,
            },
          ]
        }),
  }
}

export function parseDraft(value: unknown): Draft {
  const source = asObject(value, 'a draft')

  return {
    id: str(source, 'id'),
    threadId: str(source, 'thread_id'),
    version: num(source, 'version'),
    status: str(source, 'status'),
    kind: text(source, 'kind'),
    basedOnMessageId: optStr(source, 'based_on_message_id'),
    body: text(source, 'body'),
    editedBody: optStr(source, 'edited_body'),
    text: text(source, 'text'),
    lang: text(source, 'lang'),
    reasonCodes: strings(source, 'reason_codes'),
    preflight: parsePreflight(raw(source, 'preflight')),
    listingSnapshotIds: strings(source, 'listing_snapshot_ids'),
    photos: parseDraftPhotos(source.photos),
    createdByAiRunId: optStr(source, 'created_by_ai_run_id'),
    afterManual: bool(source, 'after_manual'),
    approvable: bool(source, 'approvable'),
    approvedAt: optTime(source, 'approved_at'),
    sentAt: optTime(source, 'sent_at'),
    failureReason: text(source, 'failure_reason'),
    createdAt: time(source, 'created_at'),
    updatedAt: time(source, 'updated_at'),
  }
}

export function parseDraftPhotos(value: unknown): readonly DraftPhoto[] {
  if (value === undefined || value === null) return []

  return asArray(value, 'photos').map((each) => {
    const source = asObject(each, 'a picture')
    return {
      assetId: str(source, 'asset_id'),
      listingId: text(source, 'listing_id'),
      url: str(source, 'url'),
      mime: text(source, 'mime'),
      caption: text(source, 'caption'),
    }
  })
}

function parseSearchRequest(value: unknown): SearchRequest | undefined {
  if (value === undefined || value === null) return undefined
  const source = asObject(value, '"search_request"')

  return {
    id: str(source, 'id'),
    status: str(source, 'status'),
    statusReason: text(source, 'status_reason'),
    version: num(source, 'version'),
    unitType: text(source, 'unit_type'),
    preferredUnitType: optStr(source, 'preferred_unit_type'),
    startDateTarget: optTime(source, 'start_date_target'),
    startDateEarliest: optTime(source, 'start_date_earliest'),
    startDateLatest: optTime(source, 'start_date_latest'),
    endDateTarget: optTime(source, 'end_date_target'),
    endDateEarliest: optTime(source, 'end_date_earliest'),
    endDateLatest: optTime(source, 'end_date_latest'),
    durationMonthsHundredths: optNum(source, 'duration_months_hundredths'),
    internalTermBand: text(source, 'internal_term_band'),
    peopleConfirmed: count(source, 'people_confirmed'),
    peoplePossible: count(source, 'people_possible'),
    budgetIdeal: parseMoney(source['budget_ideal']),
    budgetMax: parseMoney(source['budget_max']),
    budgetScope: text(source, 'budget_scope'),
    billsScope: text(source, 'bills_scope'),
    roomPrivateRequired: optBool(source, 'room_private_required'),
    completeness: optNum(source, 'completeness') ?? 0,
    missingRequired: codes(raw(source, 'missing_required')),
    riskFlags: codes(raw(source, 'risk_flags')),
    fields: objects(source, 'fields').map((field) => ({
      field: str(field, 'field'),
      value: raw(field, 'value'),
      confidence: optNum(field, 'confidence') ?? 0,
      importance: text(field, 'importance'),
      source: text(field, 'source'),
      evidenceMessageIds: strings(field, 'evidence_message_ids'),
      confirmedAt: optTime(field, 'confirmed_at'),
      createdAt: time(field, 'created_at'),
    })),
  }
}

export function fetchThreadScreen(threadId: string, signal?: AbortSignal): Promise<ThreadScreen> {
  return request(
    `/api/threads/${encodeURIComponent(threadId)}`,
    (body) => {
      const source = asObject(body, 'the conversation')
      const thread = asObject(source['thread'], '"thread"')
      const contact = source['contact']

      return {
        thread: {
          id: str(thread, 'id'),
          contactId: str(thread, 'contact_id'),
          accountId: str(thread, 'account_id'),
          mode: str(thread, 'mode'),
          humanActiveUntil: optTime(thread, 'human_active_until'),
          inScope: bool(thread, 'in_scope'),
          unreadCount: count(thread, 'unread_count'),
          lastInboundAt: optTime(thread, 'last_inbound_at'),
          lastOutboundAt: optTime(thread, 'last_outbound_at'),
          addedAt: optTime(thread, 'added_at'),
          historyFrom: optTime(thread, 'history_from'),
        },
        contact:
          contact === undefined || contact === null
            ? undefined
            : (() => {
                const card = asObject(contact, '"contact"')
                return {
                  id: str(card, 'id'),
                  displayName: text(card, 'display_name'),
                  username: optStr(card, 'username'),
                  locale: optStr(card, 'locale'),
                }
              })(),
        searchRequest: parseSearchRequest(source['search_request']),
        drafts: objects(source, 'drafts').map(parseDraft),
        tasks: objects(source, 'tasks').map((task) => ({
          id: str(task, 'id'),
          kind: text(task, 'kind'),
          priority: text(task, 'priority'),
          status: text(task, 'status'),
          dueAt: optTime(task, 'due_at'),
          reason: text(task, 'reason'),
          evidenceMessageIds: strings(task, 'evidence_message_ids'),
          createdAt: time(task, 'created_at'),
        })),
        tasksTruncated: bool(source, 'tasks_truncated'),
      }
    },
    signal === undefined ? {} : { signal },
  )
}

/**
 * The modes a person may put a conversation into.
 *
 * The column allows five. `handoff_pending` is the system saying it has already
 * stopped, and `blocked` and `closed` are decisions about the contact rather
 * than about the assistant; the endpoint answers 400 for all three. The type is
 * narrow so that the panel cannot ask for one of them by accident.
 *
 * This is the conversation's own mode, not the autonomy mode: what the
 * assistant is allowed to do at all is a setting, and this is where this one
 * conversation stands (CONTEXT.md).
 */
export type ChoosableThreadMode = 'ai_active' | 'human_paused'

/** Where a conversation moved from and to. */
export interface ThreadModeChange {
  readonly threadId: string
  readonly mode: string
  readonly previousMode: string
  /**
   * Whether a person made this change.
   *
   * Always true in the answer to this call, and sent anyway because the same
   * shape arrives on the event stream when the system moved a conversation
   * itself. Read rather than assumed, so the day the panel reads one off the
   * stream it is reading the field and not a constant.
   */
  readonly changedByAPerson: boolean
}

export interface SetThreadModeInput {
  readonly threadId: string
  readonly mode: ChoosableThreadMode
  /**
   * Why, in the person's own words, for the audit trail.
   *
   * Optional, and empty is the ordinary case: pausing is usually obvious from
   * the conversation it happened in, and a field somebody must fill in is a
   * field that fills up with full stops. Empty is left out of the body entirely
   * rather than sent as `""`.
   */
  readonly reason: string
}

/**
 * Pause the assistant on one conversation, or let it go again.
 *
 * Answers 403 without `threads.pause`, and the caller must let that reach the
 * screen: a refusal that is swallowed leaves a manager believing a conversation
 * is paused when the assistant is still drafting in it.
 */
export function setThreadMode(input: SetThreadModeInput): Promise<ThreadModeChange> {
  const reason = input.reason.trim()

  return request(
    `/api/threads/${encodeURIComponent(input.threadId)}/mode`,
    (body) => {
      const source = asObject(body, 'the mode change')
      return {
        threadId: str(source, 'thread_id'),
        mode: str(source, 'mode'),
        // Tolerated as absent: a conversation whose stored mode was never set
        // has no previous one to name, and that is not a reason to refuse the
        // answer to a change that has already happened.
        previousMode: text(source, 'previous_mode'),
        changedByAPerson: bool(source, 'changed_by_a_person'),
      }
    },
    {
      method: 'POST',
      body: reason === '' ? { mode: input.mode } : { mode: input.mode, reason },
    },
  )
}

/**
 * Clears the unread count because the conversation was opened.
 *
 *   POST /api/threads/{id}/read    threads.read
 *
 * Ours alone: this counter is the panel's own bookkeeping and Telegram is never
 * told anything was read. Until it existed the count only fell when a reply went
 * out, so a conversation read and not answered stayed bold for ever and the
 * "Непрочитанные" tab never emptied.
 */
export async function markThreadRead(threadId: string): Promise<void> {
  return requestEmpty(`/api/threads/${encodeURIComponent(threadId)}/read`, { method: 'POST' })
}

/**
 * Closes one piece of work waiting for a person.
 *
 *   POST /api/tasks/{id}/resolve    threads.read
 *
 * Without it a task was a one-way door: «сессия аккаунта потеряна» stayed on the
 * conversation after the account was reconnected, and because the store refuses
 * a second task of the same kind on the same conversation, an open one swallowed
 * every later task like it.
 */
export async function resolveTask(taskId: string, resolution: string): Promise<void> {
  return requestEmpty(`/api/tasks/${encodeURIComponent(taskId)}/resolve`, {
    method: 'POST',
    body: { resolution },
  })
}

/**
 * Sends a reply the manager wrote themselves.
 *
 *   POST /api/threads/{id}/messages    drafts.approve
 *
 * The words become a draft whose author is a person and whose approval is the
 * act of writing it, and it leaves by the one road out (invariant 5). Nothing is
 * returned but the draft: whether it actually reached the client is the sender's
 * answer, and the conversation shows it when it does.
 */
export async function sendManualReply(threadId: string, text: string): Promise<void> {
  return requestEmpty(`/api/threads/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    body: { text },
  })
}
